import { FeatureFlags } from '@lightdash/common';
import { Stack } from '@mantine-8/core';
import { useMemo, type FC } from 'react';
import { Navigate, useParams, useRoutes, type RouteObject } from 'react-router';
import { DocumentTitle } from '../components/common/DocumentTitle';
import ErrorState from '../components/common/ErrorState';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import SuboptimalState from '../components/common/SuboptimalState/SuboptimalState';
import CompilationHistory from '../components/CompilationHistory';
import { DataOps } from '../components/DataOps';
import { DefaultUserSpaces } from '../components/DefaultUserSpaces';
import PreAggregateAudit from '../components/PreAggregateAudit';
import PreAggregateMaterializations from '../components/PreAggregateMaterializations';
import ProjectUserAccess from '../components/ProjectAccess';
import { UpdateProjectConnection } from '../components/ProjectConnection';
import ProjectParameters from '../components/ProjectParameters';
import ProjectTablesConfiguration from '../components/ProjectTablesConfiguration/ProjectTablesConfiguration';
import SettingsQueryTimezone from '../components/SettingsQueryTimezone';
import SettingsScheduler from '../components/SettingsScheduler';
import SettingsUsageAnalytics from '../components/SettingsUsageAnalytics';
import { SettingsValidator } from '../components/SettingsValidator';
import SettingsEmbed from '../ee/features/embed/SettingsEmbed';
import { ProjectChangesets } from '../features/changesets/components/ProjectChangesets';
import RecentlyDeletedPage from '../features/recentlyDeleted/components/RecentlyDeletedPage';
import { useProject } from '../hooks/useProject';
import { useServerFeatureFlag } from '../hooks/useServerOrClientFeatureFlag';
import useApp from '../providers/App/useApp';

const ProjectSettings: FC = () => {
    const { projectUuid } = useParams<{
        projectUuid: string;
    }>();

    const { health } = useApp();
    const { isInitialLoading, data: project, error } = useProject(projectUuid);

    const isSoftDeleteEnabled = health.data?.softDelete?.enabled ?? false;
    const { data: defaultUserSpacesFlag } = useServerFeatureFlag(
        FeatureFlags.DefaultUserSpaces,
    );
    const isDefaultUserSpacesEnabled = defaultUserSpacesFlag?.enabled ?? false;

    const routes = useMemo<RouteObject[]>(() => {
        if (!projectUuid) {
            return [];
        }
        return [
            {
                path: `/settings`,
                element: <UpdateProjectConnection projectUuid={projectUuid} />,
            },
            {
                path: `/tablesConfiguration`,
                element: (
                    <ProjectTablesConfiguration projectUuid={projectUuid} />
                ),
            },
            {
                path: `/changesets`,
                element: <ProjectChangesets projectUuid={projectUuid} />,
            },
            {
                path: `/projectAccess`,
                element: <ProjectUserAccess projectUuid={projectUuid} />,
            },
            {
                path: `/usageAnalytics`,
                element: <SettingsUsageAnalytics projectUuid={projectUuid} />,
            },
            {
                path: `/scheduledDeliveries`,
                element: <SettingsScheduler projectUuid={projectUuid} />,
            },
            {
                path: `/validator`,
                element: <SettingsValidator projectUuid={projectUuid} />,
            },
            {
                path: `/dataOps`,
                element: <DataOps projectUuid={projectUuid} />,
            },
            ...(isDefaultUserSpacesEnabled
                ? [
                      {
                          path: `/defaultUserSpaces`,
                          element: (
                              <DefaultUserSpaces projectUuid={projectUuid} />
                          ),
                      },
                  ]
                : []),
            ...(isSoftDeleteEnabled
                ? [
                      {
                          path: `/recentlyDeleted`,
                          element: (
                              <RecentlyDeletedPage projectUuid={projectUuid} />
                          ),
                      },
                  ]
                : []),
            {
                path: `/parameters`,
                element: <ProjectParameters projectUuid={projectUuid} />,
            },
            {
                path: `/queryTimezone`,
                element: <SettingsQueryTimezone projectUuid={projectUuid} />,
            },
            {
                path: `/compilationHistory`,
                element: <CompilationHistory projectUuid={projectUuid} />,
            },
            {
                path: `/preAggregates`,
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate
                                to={`/generalSettings/projectManagement/${projectUuid}/preAggregates/audit`}
                                replace
                            />
                        ),
                    },
                    {
                        path: `materializations`,
                        element: (
                            <PreAggregateMaterializations
                                projectUuid={projectUuid}
                            />
                        ),
                    },
                    {
                        path: `audit`,
                        element: (
                            <PreAggregateAudit projectUuid={projectUuid} />
                        ),
                    },
                ],
            },
            {
                path: '*',
                element: <Navigate to={`/generalSettings`} />,
            },
            {
                path: '/embed', // commercial route
                element: <SettingsEmbed projectUuid={projectUuid} />,
            },
        ];
    }, [projectUuid, isSoftDeleteEnabled, isDefaultUserSpacesEnabled]);
    const routesElements = useRoutes(routes);

    if (error) {
        return <ErrorState error={error.error} />;
    }

    if (isInitialLoading || !project || !projectUuid) {
        return (
            <div style={{ marginTop: '20px' }}>
                <SuboptimalState title="Loading project" loading />
            </div>
        );
    }

    return (
        <>
            <DocumentTitle title="Project Settings" />

            <Stack gap="xl">
                <PageBreadcrumbs
                    items={[
                        {
                            title: 'All projects',
                            to: '/generalSettings/projectManagement',
                        },
                        {
                            title: project.name,
                            active: true,
                        },
                    ]}
                />
                {routesElements}
            </Stack>
        </>
    );
};

export default ProjectSettings;
